import { useEffect, useState } from 'react';
import {
  getNetworkState,
  INITIAL_NETWORK_STATE,
  isUnmetered,
  NetworkState,
  subscribeToNetworkState,
} from '@/utils/networkState';

export interface UseNetworkStateResult extends NetworkState {
  /** True while the first reading is still outstanding. */
  isResolving: boolean;
  /** Convenience for the common case: definitely no connection. */
  isOffline: boolean;
  /** Safe to spend real bandwidth without asking. */
  isUnmetered: boolean;
}

/**
 * The current connection, kept live.
 *
 * Starts optimistic (see INITIAL_NETWORK_STATE) so nothing is disabled during
 * the first tick. Callers that want to avoid flashing an offline message
 * before the truth arrives can gate on `isResolving`.
 */
export const useNetworkState = (): UseNetworkStateResult => {
  const [state, setState] = useState<NetworkState>(INITIAL_NETWORK_STATE);
  const [isResolving, setIsResolving] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void getNetworkState().then((initial) => {
      if (cancelled) return;
      setState(initial);
      setIsResolving(false);
    });

    const unsubscribe = subscribeToNetworkState((next) => {
      if (cancelled) return;
      setState(next);
      setIsResolving(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return {
    ...state,
    isResolving,
    isOffline: !state.isOnline,
    isUnmetered: isUnmetered(state),
  };
};
